var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { uniq } from 'lodash';
import { useLocale } from '@adsk/alloy-react-locale';
import { AlertWarningIcon } from '@adsk/alloy-react-icon';
import styled from 'styled-components';
import theme from '@adsk/alloy-react-theme';
import { LinkButton } from '@adsk/alloy-react-button';
import { VirtualizableTreeNode } from '@adsk/alloy-react-tree';
import ContentLoader from '@adsk/alloy-react-content-loader';
import { useLocations } from '../../LocationProvider/LocationProvider';
import LocationTreeNode from './LocationTreeNode';
import { getNodeAncestors, getNodeDescendants, } from '../../helpers/treeHelpers';
import { MAX_SELECTED_NODES } from '../../consts';
import { useVirtual } from 'react-virtual';
var ErrorMessage = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  ", "\n  color: ", ";\n  padding: 12px 16px;\n  text-align: center;\n"], ["\n  ", "\n  color: ", ";\n  padding: 12px 16px;\n  text-align: center;\n"])), __assign({}, theme.typography.bodyMedium), theme.colors.charcoal500);
ErrorMessage.displayName = 'ErrorMessage';
var NotFoundText = styled.span(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  ", "\n  color: ", ";\n  padding: 12px 16px;\n"], ["\n  ", "\n  color: ", ";\n  padding: 12px 16px;\n"])), __assign({}, theme.typography.bodyMedium), theme.colors.charcoal500);
NotFoundText.displayName = 'NotFoundText';
var LocationsDropdownTreeItems = function (_a) {
    var menuItems = _a.menuItems, getTreeNodeProps = _a.getTreeNodeProps, includeSubLocation = _a.includeSubLocation, selectedNodes = _a.selectedNodes, multiple = _a.multiple;
    var T = useLocale(function (_a) {
        var locale = _a.locale;
        return require("../i18n/".concat(locale, ".json"));
    }).T;
    var _b = useLocations(), treeDataMap = _b.treeDataMap, isLoading = _b.isLoading, isError = _b.isError, retryLoad = _b.retryLoad;
    var _c = __read(useState(new Map([])), 2), ancestorsOfSelected = _c[0], setAncestorsOfSelected = _c[1];
    var _d = __read(useState([]), 2), includedSubLocations = _d[0], setIncludedSubLocations = _d[1];
    // maintain selectedSubLocations array on selection or include sub locations change
    // (required for disabling selection and setting checked state on included sub locations)
    useEffect(function () {
        if (!multiple)
            return;
        if (!includeSubLocation) {
            setIncludedSubLocations([]);
        }
        else {
            var selectedChildren_1 = [];
            selectedNodes.forEach(function (node) {
                return selectedChildren_1.push.apply(selectedChildren_1, __spreadArray([], __read(getNodeDescendants(treeDataMap, node).map(function (_a) {
                    var id = _a.id;
                    return id;
                })), false));
            });
            setIncludedSubLocations(uniq(selectedChildren_1));
        }
    }, [selectedNodes, includeSubLocation, treeDataMap, multiple]);
    // Updated ancestors on selection change (required for intermidiate checkbox status)
    useEffect(function () {
        var newAncestorsOfSelected = new Map();
        selectedNodes.forEach(function (node) {
            var ancestors = [];
            if (ancestorsOfSelected.has(node.id)) {
                ancestors = __spreadArray([], __read(ancestorsOfSelected.get(node.id)), false);
            }
            else {
                ancestors = getNodeAncestors(treeDataMap, node).map(function (_a) {
                    var id = _a.id;
                    return id;
                });
            }
            newAncestorsOfSelected.set(node.id, ancestors);
        });
        setAncestorsOfSelected(newAncestorsOfSelected);
    }, [selectedNodes, treeDataMap]);
    var getSelectionProps = useCallback(function (node) {
        var isSelected = false;
        var isDisabled = false;
        var currentAncestorsOfSelected = Array.from(ancestorsOfSelected.values()).flat();
        if (selectedNodes.map(function (_a) {
            var id = _a.id;
            return id;
        }).includes(node.id) ||
            includedSubLocations.includes(node.id)) {
            isSelected = true;
        }
        else if (multiple && currentAncestorsOfSelected.includes(node.id)) {
            isSelected = 'indeterminate';
        }
        if ((isSelected !== true && selectedNodes.length >= MAX_SELECTED_NODES) ||
            (includeSubLocation && includedSubLocations.includes(node.id))) {
            isDisabled = true;
        }
        return { isDisabled: isDisabled, isSelected: isSelected };
    }, [
        selectedNodes,
        ancestorsOfSelected,
        includeSubLocation,
        includedSubLocations,
        multiple,
    ]);
    var parentRef = useRef(null);
    var nodesToDisplay = menuItems.filter(function (node) { return node.show; });
    var _e = useVirtual({
        size: nodesToDisplay.length,
        estimateSize: useCallback(function () { return 40; }, []),
        parentRef: parentRef,
        overscan: 25,
    }), totalSize = _e.totalSize, virtualItems = _e.virtualItems;
    if (isLoading) {
        return (
        // @ts-ignore: ts is saying that ContentLoader is not a JSX element even though it's not true
        React.createElement(ContentLoader, { width: 227, height: 66, style: { padding: '12px 16px' } },
            React.createElement("rect", { x: "0", y: "8", rx: "6", ry: "6", width: "66", height: "8" }),
            React.createElement("rect", { x: "0", y: "32", rx: "6", ry: "6", width: "227", height: "8" }),
            React.createElement("rect", { x: "0", y: "56", rx: "6", ry: "6", width: "156", height: "8" })));
    }
    if (isError) {
        return (React.createElement(ErrorMessage, null,
            React.createElement(AlertWarningIcon, { style: { marginRight: '5px', verticalAlign: 'middle' } }),
            React.createElement("span", null, "Could not load data. "),
            React.createElement(LinkButton, { onClick: retryLoad }, T.translate('locationPicker.retryTreeLoadOnError'))));
    }
    if (!menuItems.length) {
        return (React.createElement(NotFoundText, null, T.translate('locationPicker.noResults')));
    }
    return (React.createElement("div", { ref: parentRef, style: {
            position: 'relative',
            height: totalSize,
            overflowY: 'auto',
        } }, virtualItems.map(function (_a) {
        var index = _a.index, key = _a.key, size = _a.size, start = _a.start;
        var treeNodeProps = getTreeNodeProps(nodesToDisplay[index]);
        var selectionProps = getSelectionProps(nodesToDisplay[index].original);
        return (React.createElement(VirtualizableTreeNode, __assign({ key: key, size: size, start: start }, treeNodeProps, { isMultiSelectable: multiple, isSingleSelectable: !multiple, style: {
                width: 'calc(100% - 20px)', // The li has a padding 10px defined on left and right, causing overflow-x when virtualized (because of the absolute position)
            } }),
            React.createElement(LocationTreeNode, { node: nodesToDisplay[index].original, includeSubLocation: includeSubLocation, multiple: multiple, ancestorsOfSelected: ancestorsOfSelected, explicitlySelectedNodesIds: selectedNodes.map(function (_a) {
                    var id = _a.id;
                    return id;
                }), isDisabled: selectionProps.isDisabled, isSelected: selectionProps.isSelected, isExpandable: treeNodeProps.isExpandable, isExpanded: treeNodeProps.isExpanded, onExpand: treeNodeProps.onExpand })));
    })));
};
export default LocationsDropdownTreeItems;
var templateObject_1, templateObject_2;
